import { fixedUi } from '@/i18n/fixedUi';
import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import PlatformProductManagePage from '@/pages/ipe/workbench/PlatformProductManagePage';
import { ProductFilterCard, ProductListCard } from '@/shein-platform/modules/products/components/ProductPageCards';
import SheinOpsListToolbar, { downloadCsv } from '@/shein-platform/components/SheinOpsListToolbar';
import SheinSyncProgressSlot from '@/shein-platform/components/SheinSyncProgressSlot';
import SheinProductSyncModal from '@/shein-platform/components/SheinProductSyncModal';
import BatchQueryModal from '@/shein-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  joinBatchTokens,
  parseBatchTokens,
} from '@/shein-platform/modules/products/components/batchQueryUtils';
import { useSheinShop } from '@/shein-platform/context/SheinShopContext';
import { useSheinSyncJobs } from '@/shein-platform/context/SheinSyncJobContext';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import { formatSiteListSyncedAt } from '@/shein-platform/utils/sheinSiteList';
import { showSheinConfirm } from '@/shein-platform/components/SheinConfirmDialog';
import {
  fetchAllPages,
} from '@/shein-platform/utils/sheinExportHelpers';
import { buildOpsExportMenu, opsExportLabel } from '@/shein-platform/utils/buildOpsExportMenu';
import PlatformShopDimensionTabs from '@/components/platform/PlatformShopDimensionTabs';
import PlatformShopPickModal from '@/components/platform/PlatformShopPickModal';
import {
  PLATFORM_ALL_SHOPS_ID,
  isAllShopsScope,
  isConcreteShopId,
  resolveConcreteShopId,
} from '@/utils/platformShopScope';
import { showToast } from '@/utils/toast';
import { readNoticeProductQuery } from '@/shein-platform/utils/sheinNoticeFocusQuery';

const PRODUCT_EXPORT_HEADERS = [
  'id', 'title', 'platformSpuId', 'platformSkcId', 'platformSkuId',
  'publishStatus', 'priceReviewStatus', 'salePrice', 'supplyPrice',
  'stockQty', 'salesLast7Days', 'createdAt', 'publishedAt', 'listedAt', 'lastSyncedAt',
];

export { parseBatchTokens, BATCH_QUERY_LIMIT } from './batchQueryUtils';

const STATUS_TABS = [
  { id: 'all', label: '全部', statKey: 'total' },
  { id: 'listed', label: '已上架', statKey: 'listed' },
  { id: 'pending', label: '待上架', statKey: 'pending' },
  { id: 'sold_out', label: '已售罄', statKey: 'soldOut' },
  { id: 'offline', label: '已下架', statKey: 'offline' },
];

function statusTabCount(stats, tab) {
  if (!stats) return 0;
  // 「全部」用后端去重 SPU 总数；各维度相加会因同一 SPU 跨状态 SKU 重复计数
  if (tab.id === 'all') return Number(stats.total) || 0;
  return Number(stats[tab.statKey]) || 0;
}

const ID_TYPE_OPTIONS = [
  { value: 'skc', label: 'SKC' },
  { value: 'spu', label: 'SPU' },
  { value: 'sku', label: 'SKU' },
];

const EMPTY_FILTERS = {
  idType: 'skc',
  idValue: '',
  supplierCode: '',
  brand: '',
};

const EMPTY_QUERY = {
  idType: 'skc',
  keywords: '',
  brand: '',
  supplierCode: '',
};

const BATCH_FIELDS = {
  idValue: {
    titleKey: 'idType',
    titleMap: { skc: '批量查询 SKC', spu: '批量查询 SPU', sku: '批量查询 SKU' },
    placeholder: '每行一个编码，或用逗号分隔',
  },
  supplierCode: {
    title: '批量查询货号',
    placeholder: '每行一个货号，或用逗号分隔',
  },
  brand: {
    title: '批量查询品牌',
    placeholder: '每行一个品牌，或用逗号分隔',
  },
};

/**
 * 产品目录面板
 */
export default function ProductCatalogPanel({
  mode = 'catalog',
  showStatusTabs = true,
}) {
  const {
    activeShopId,
    activeShop,
    scopedShops,
    expectedShopType,
    isAllShopsMode,
    loading: shopLoading,
  } = useSheinShop();
  const { isBusy } = useSheinSyncJobs();
  const [searchParams] = useSearchParams();
  const noticeFocusKey = searchParams.toString();
  const [pageShopScope, setPageShopScope] = useState(() => activeShopId || '');
  const [filterDraft, setFilterDraft] = useState(EMPTY_FILTERS);
  const [query, setQuery] = useState(EMPTY_QUERY);
  const [, setQueryNonce] = useState(0);
  const [listingDimension, setListingDimension] = useState(
    mode === 'drafts' ? 'drafts' : mode === 'price_review' ? 'price_review' : mode === 'records' ? 'listed' : 'listed',
  );
  const [dimensionStats, setDimensionStats] = useState(null);
  const [selectedCount, setSelectedCount] = useState(0);
  const selectedIdsRef = useRef([]);
  const selectedSpuIdsRef = useRef([]);
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchModalField, setBatchModalField] = useState(null);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncModalConfirming, setSyncModalConfirming] = useState(false);
  const [shopPickOpen, setShopPickOpen] = useState(false);
  const [pendingSyncPayload, setPendingSyncPayload] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const [syncState, setSyncState] = useState({
    refreshing: false,
    listLoading: false,
    listTotal: 0,
    listPageCount: 0,
    cooldownSeconds: 0,
    refresh: null,
    lastRefreshedAt: '',
    exportSelected: null,
    exportPage: null,
    batchShelfOn: null,
    batchShelfOff: null,
    clearSelection: null,
  });

  const productSyncBusy = isBusy('product', isConcreteShopId(pageShopScope) ? pageShopScope : activeShopId);

  useEffect(() => {
    setPageShopScope(activeShopId || '');
  }, [activeShopId]);

  useEffect(() => {
    const notice = readNoticeProductQuery(searchParams);
    const nextDim = mode === 'drafts'
      ? 'drafts'
      : mode === 'price_review'
        ? 'price_review'
        : mode === 'records'
          ? 'listed'
          : (notice?.dim || 'listed');
    setListingDimension(nextDim);
    if (notice?.keywords) {
      setFilterDraft({
        ...EMPTY_FILTERS,
        idType: notice.idType || 'skc',
        idValue: notice.keywords,
      });
      setQueryNonce((n) => {
        const next = n + 1;
        setQuery({
          idType: notice.idType || 'skc',
          keywords: notice.keywords,
          brand: '',
          supplierCode: '',
          _nonce: next,
        });
        return next;
      });
    } else {
      setFilterDraft(EMPTY_FILTERS);
      setQueryNonce((n) => {
        const next = n + 1;
        setQuery({ ...EMPTY_QUERY, _nonce: next });
        return next;
      });
    }
    setSelectedCount(0);
    selectedIdsRef.current = [];
    selectedSpuIdsRef.current = [];
    setBatchModalField(null);
    setPersistedSyncedAt('');
  }, [mode, activeShopId, noticeFocusKey, searchParams]);

  useEffect(() => {
    if (!activeShopId) {
      setPersistedSyncedAt('');
      return undefined;
    }
    let cancelled = false;
    sheinPlatformAPI.getShopSyncStatus(activeShopId)
      .then((res) => {
        if (cancelled) return;
        const modules = Array.isArray(res?.modules) ? res.modules : [];
        const product = modules.find((m) => String(m.module || '').toUpperCase() === 'PRODUCT');
        const at = product?.lastSyncEndAt || product?.lastRunAt || '';
        setPersistedSyncedAt(at ? String(at) : '');
      })
      .catch(() => {
        if (!cancelled) setPersistedSyncedAt('');
      });
    return () => { cancelled = true; };
  }, [activeShopId]);

  const latestSyncedAt = syncState.lastRefreshedAt || persistedSyncedAt;
  const latestSyncedLabel = latestSyncedAt
    ? `最近更新：${formatSiteListSyncedAt(latestSyncedAt)}`
    : '尚未同步';

  const onQuery = () => {
    const idTokens = parseBatchTokens(filterDraft.idValue).slice(0, BATCH_QUERY_LIMIT);
    const supplierTokens = parseBatchTokens(filterDraft.supplierCode).slice(0, BATCH_QUERY_LIMIT);
    const brandTokens = parseBatchTokens(filterDraft.brand).slice(0, BATCH_QUERY_LIMIT);
    setSyncState((prev) => ({ ...prev, listLoading: true }));
    setQueryNonce((n) => {
      const next = n + 1;
      setQuery({
        idType: filterDraft.idType || 'skc',
        keywords: idTokens.join(','),
        brand: brandTokens.join(','),
        supplierCode: supplierTokens.join(','),
        _nonce: next,
      });
      return next;
    });
  };

  const onReset = () => {
    setFilterDraft(EMPTY_FILTERS);
    setQueryNonce((n) => {
      const next = n + 1;
      setQuery({ ...EMPTY_QUERY, _nonce: next });
      return next;
    });
    if (mode === 'catalog') setListingDimension('listed');
  };

  const updateField = (key, value) => {
    setFilterDraft((prev) => ({ ...prev, [key]: value }));
  };

  const openBatchModal = (fieldKey) => {
    setBatchModalField(fieldKey);
  };

  const onBatchConfirm = (tokens) => {
    if (!batchModalField) return;
    updateField(batchModalField, joinBatchTokens(tokens));
  };

  const batchModalMeta = batchModalField ? BATCH_FIELDS[batchModalField] : null;
  const batchModalTitle = batchModalMeta
    ? (batchModalMeta.title
      || batchModalMeta.titleMap?.[filterDraft[batchModalMeta.titleKey]]
      || fixedUi("批量查询"))
    : '批量查询';

  // 仅同步展示态；回调走 ref，避免父组件因函数引用变化反复重渲染
  const handleSyncState = useCallback((state) => {
    setSyncState((prev) => {
      const nextLast = state?.lastRefreshedAt || prev.lastRefreshedAt || '';
      const next = {
        ...prev,
        lastRefreshedAt: nextLast,
        refreshing: Boolean(state?.refreshing),
        listLoading: Boolean(state?.listLoading),
        listTotal: Number.isFinite(Number(state?.listTotal)) ? Number(state.listTotal) : prev.listTotal,
        listPageCount: Number.isFinite(Number(state?.listPageCount)) ? Number(state.listPageCount) : prev.listPageCount,
        cooldownSeconds: state?.cooldownSeconds || 0,
        refresh: state?.refresh ?? prev.refresh,
        reloadList: state?.reloadList ?? prev.reloadList,
        exportSelected: state?.exportSelected ?? prev.exportSelected,
        exportPage: state?.exportPage ?? prev.exportPage,
        batchShelfOn: state?.batchShelfOn ?? prev.batchShelfOn,
        batchShelfOff: state?.batchShelfOff ?? prev.batchShelfOff,
        clearSelection: state?.clearSelection ?? prev.clearSelection,
      };
      if (
        next.lastRefreshedAt === prev.lastRefreshedAt
        && next.refreshing === prev.refreshing
        && next.listLoading === prev.listLoading
        && next.listTotal === prev.listTotal
        && next.listPageCount === prev.listPageCount
        && next.cooldownSeconds === prev.cooldownSeconds
        && next.refresh === prev.refresh
        && next.reloadList === prev.reloadList
        && next.exportSelected === prev.exportSelected
        && next.exportPage === prev.exportPage
        && next.batchShelfOn === prev.batchShelfOn
        && next.batchShelfOff === prev.batchShelfOff
        && next.clearSelection === prev.clearSelection
      ) {
        return prev;
      }
      return next;
    });
    if (state?.lastRefreshedAt) {
      setPersistedSyncedAt(String(state.lastRefreshedAt));
    }
  }, []);

  const handleSelectionChange = useCallback((payload) => {
    if (Array.isArray(payload)) {
      selectedIdsRef.current = payload;
      selectedSpuIdsRef.current = [];
      setSelectedCount(payload.length);
      return;
    }
    const ids = Array.isArray(payload?.ids) ? payload.ids : [];
    const spuIds = Array.isArray(payload?.spuIds) ? payload.spuIds : [];
    selectedIdsRef.current = ids;
    selectedSpuIdsRef.current = spuIds;
    const spuCount = Number(payload?.count);
    setSelectedCount(Number.isFinite(spuCount) && spuCount >= 0 ? spuCount : spuIds.length);
  }, []);

  const buildProductListParams = useCallback(() => {
    const all = isAllShopsScope(pageShopScope);
    return {
      shopId: all ? undefined : (pageShopScope || undefined),
      allOfType: all ? true : undefined,
      shopType: all ? (expectedShopType || undefined) : undefined,
      priceReviewStatus: 'all',
      publishStatus: 'all',
      listingDimension: mode === 'catalog' ? listingDimension : undefined,
      idType: query?.idType || undefined,
      keywords: query?.keywords || undefined,
      brand: query?.brand || undefined,
      supplierCode: query?.supplierCode || undefined,
    };
  }, [pageShopScope, expectedShopType, listingDimension, mode, query]);

  const listReady = Boolean(pageShopScope)
    && (isConcreteShopId(pageShopScope) || (isAllShopsScope(pageShopScope) && expectedShopType));

  const writeProductsCsv = useCallback((rows, filename) => {
    downloadCsv(
      filename,
      PRODUCT_EXPORT_HEADERS,
      rows.map((row) => ({
        id: row.id,
        title: row.title,
        platformSpuId: row.platformSpuId,
        platformSkcId: row.platformSkcId,
        platformSkuId: row.platformSkuId,
        publishStatus: row.publishStatus,
        priceReviewStatus: row.priceReviewStatus,
        salePrice: row.salePrice,
        supplyPrice: row.supplyPrice,
        stockQty: row.stockQty,
        salesLast7Days: row.salesLast7Days,
        createdAt: row.createdAt,
        publishedAt: row.publishedAt,
        listedAt: row.listedAt,
        lastSyncedAt: row.lastSyncedAt,
      })),
    );
  }, []);

  const fetchAllProducts = useCallback(async () => {
    const base = buildProductListParams();
    return fetchAllPages(async (page, pageSize) => {
      const res = await sheinPlatformAPI.listProducts({ ...base, page, pageSize });
      return {
        items: Array.isArray(res?.items) ? res.items : [],
        total: Number(res?.total) || 0,
      };
    });
  }, [buildProductListParams]);

  const exportFilteredProducts = useCallback(async () => {
    if (!listReady) {
      showToast(fixedUi("请先选择店铺"), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const rows = await fetchAllProducts();
      if (!rows.length) {
        showToast(fixedUi("暂无可导出的商品"), 'warning');
        return;
      }
      writeProductsCsv(rows, `shein-products-filtered-${Date.now()}.csv`);
      showToast(`已按筛选导出 ${rows.length} 条`, 'success');
    } catch (e) {
      showToast(e?.message || fixedUi("导出筛选失败"), 'error');
    } finally {
      setExporting(false);
    }
  }, [listReady, exporting, fetchAllProducts, writeProductsCsv]);

  const exportPageProducts = useCallback(() => {
    if (typeof syncState.exportPage === 'function') {
      syncState.exportPage();
      return;
    }
    showToast(fixedUi("当前页没有可导出的商品"), 'warning');
  }, [syncState.exportPage]);

  const exportSelectedProducts = useCallback(() => {
    if (typeof syncState.exportSelected === 'function') {
      syncState.exportSelected();
      return;
    }
    showToast(fixedUi("请先勾选要导出的商品"), 'warning');
  }, [syncState.exportSelected]);

  const listTotal = Number(syncState.listTotal) || 0;
  const listPageCount = Number(syncState.listPageCount) || 0;

  const onBatchDelete = async () => {
    const spuIds = (selectedSpuIdsRef.current || [])
      .map((s) => String(s || '').trim())
      .filter((s) => s && s !== '—');
    if (!activeShopId || !spuIds.length) {
      showToast(fixedUi("请先勾选要删除的本地商品（按 SPU）"), 'warning');
      return;
    }
    const ok = await showSheinConfirm({
      title: '批量删除',
      message: `将按 SPU 删除 ${spuIds.length} 个本地商品缓存及其下全部 SKU（仅清本系统，不会删除 SHEIN 平台店铺商品）。`,
      tone: 'danger',
      confirmText: '删除',
      cancelText: '取消',
    });
    if (!ok) return;
    setBatchBusy(true);
    try {
      const res = await sheinPlatformAPI.batchDeleteProducts(activeShopId, { spuNames: spuIds });
      const deletedSpus = Number(res?.deletedSpus);
      const deleted = Number(res?.deleted);
      if (Number.isFinite(deletedSpus) && deletedSpus > 0) {
        showToast(`已删除 ${deletedSpus} 个 SPU（共 ${deleted || 0} 条 SKU 缓存）`, 'success');
      } else if (Number.isFinite(deleted) && deleted > 0) {
        showToast(`已删除 ${deleted} 条本地商品缓存`, 'success');
      } else {
        showToast(fixedUi("未删除任何缓存（可能已删除或不存在）"), 'warning');
      }
      selectedIdsRef.current = [];
      selectedSpuIdsRef.current = [];
      setSelectedCount(0);
      syncState.clearSelection?.();
      // 立刻展示加载动画，再刷新列表与维度数量
      setSyncState((prev) => ({ ...prev, listLoading: true }));
      try {
        await (syncState.reloadList?.() ?? Promise.resolve());
      } finally {
        setSyncState((prev) => ({ ...prev, listLoading: false }));
      }
    } catch (e) {
      showToast(e?.message || '批量删除失败', 'error');
    } finally {
      setBatchBusy(false);
    }
  };

  const shopTabs = useMemo(() => {
    // 仅门禁选「全部」进入时展示；单店登录锁定当前店
    if (!isAllShopsMode || !scopedShops.length) return null;
    return (
      <PlatformShopDimensionTabs
        shops={scopedShops}
        value={pageShopScope || PLATFORM_ALL_SHOPS_ID}
        onChange={setPageShopScope}
        showAll={scopedShops.length >= 2}
        ariaLabel="店铺维度"
      />
    );
  }, [isAllShopsMode, scopedShops, pageShopScope]);

  const tabs = useMemo(() => {
    if (!showStatusTabs || mode !== 'catalog') return shopTabs;
    return (
      <>
        {shopTabs}
        <div className="spo-status-tabs" role="tablist" aria-label={fixedUi("产品状态")}>
          {STATUS_TABS.map((tab) => {
            const count = statusTabCount(dimensionStats, tab);
            const active = listingDimension === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={`spo-status-tab${active ? ' spo-status-tab--active' : ''}`}
                onClick={() => {
                  startTransition(() => setListingDimension(tab.id));
                }}
              >
                {fixedUi(tab.label)}
                <span className="spo-status-tab__count">{count}</span>
              </button>
            );
          })}
        </div>
      </>
    );
  }, [showStatusTabs, mode, dimensionStats, listingDimension, shopTabs]);

  const idTypeLabel = ID_TYPE_OPTIONS.find((o) => o.value === filterDraft.idType)?.label || 'SKC';

  return (
    <>
      <SheinSyncProgressSlot kinds={['product']} />
      <ProductFilterCard
        toolbar={(
          <SheinOpsListToolbar
            selectedCount={selectedCount}
            busy={batchBusy || exporting}
            syncKinds={['product']}
            onSync={() => {
              if (!listReady) {
                showToast(fixedUi("请先在右上角选择已授权的 SHEIN 店铺"), 'warning');
                return;
              }
              if (productSyncBusy) {
                showToast(fixedUi("当前店铺正在同步中，请等待同步完成后可操作"), 'warning');
                return;
              }
              if (isAllShopsScope(pageShopScope) || isAllShopsMode) {
                setPendingSyncPayload(null);
                setShopPickOpen(true);
                return;
              }
              setSyncModalOpen(true);
            }}
            syncing={syncState.refreshing || productSyncBusy}
            syncCooldownSeconds={syncState.cooldownSeconds}
            syncDisabled={shopLoading || !listReady || !syncState.refresh || productSyncBusy}
            lastSyncedLabel={latestSyncedLabel}
            syncHint="从 SHEIN 后台拉取店铺商品列表与详情，更新到 IPE 本地产品库。"
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!listReady || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: listTotal,
              pageCount: listPageCount,
              selectedCount,
              onExportFiltered: exportFilteredProducts,
              onExportPage: exportPageProducts,
              onExportSelected: exportSelectedProducts,
              filteredDisabled: !listReady,
              pageDisabled: !listReady,
            })}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!activeShopId || batchBusy}
            extraActions={[
              {
                key: 'batch-shelf',
                label: selectedCount > 0 ? `批量(${selectedCount})` : '批量',
                disabled: selectedCount === 0,
                menu: [
                  {
                    key: 'shelf-on',
                    label: '上架',
                    count: selectedCount,
                    disabled: selectedCount === 0,
                    onClick: () => {
                      if (!selectedCount) {
                        showToast(fixedUi("请先勾选要上架的商品"), 'warning');
                        return;
                      }
                      syncState.batchShelfOn?.();
                    },
                  },
                  {
                    key: 'shelf-off',
                    label: '下架',
                    count: selectedCount,
                    disabled: selectedCount === 0,
                    onClick: () => {
                      if (!selectedCount) {
                        showToast(fixedUi("请先勾选要下架的商品"), 'warning');
                        return;
                      }
                      syncState.batchShelfOff?.();
                    },
                  },
                ],
              },
            ]}
          />
        )}
      >
        <div className="spo-filter-bar">
          <div className="spo-filter-bar__fields">
            <div className="spo-field spo-field--inline spo-field--id-combo spo-field--batch">
              <select
                className="spo-select spo-select--compact"
                value={filterDraft.idType}
                onChange={(e) => updateField('idType', e.target.value)}
                aria-label={fixedUi("编码类型")}
              >
                {ID_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <input
                className="spo-input"
                value={filterDraft.idValue}
                placeholder={`${idTypeLabel}，支持逗号批量`}
                onChange={(e) => updateField('idValue', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="spo-batch-add-btn"
                aria-label={`批量输入${idTypeLabel}`}
                title={fixedUi("批量查询")}
                onClick={() => openBatchModal('idValue')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>

            <div className="spo-field spo-field--inline spo-field--batch">
              <span className="spo-field__label">{fixedUi("货号")}</span>
              <input
                className="spo-input"
                value={filterDraft.supplierCode}
                placeholder={fixedUi("货号，支持逗号批量")}
                onChange={(e) => updateField('supplierCode', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="spo-batch-add-btn"
                aria-label={fixedUi("批量输入货号")}
                title={fixedUi("批量查询")}
                onClick={() => openBatchModal('supplierCode')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>

            <div className="spo-field spo-field--inline spo-field--batch">
              <span className="spo-field__label">{fixedUi("品牌")}</span>
              <input
                className="spo-input"
                value={filterDraft.brand}
                placeholder={fixedUi("品牌，支持逗号批量")}
                onChange={(e) => updateField('brand', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="spo-batch-add-btn"
                aria-label={fixedUi("批量输入品牌")}
                title={fixedUi("批量查询")}
                onClick={() => openBatchModal('brand')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="spo-filter-bar__actions">
            <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={onReset}>
              {fixedUi("重置")}
            </button>
            <button type="button" className="spo-btn spo-btn--primary spo-btn--sm" onClick={onQuery}>
              {fixedUi("查询")}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      {productSyncBusy ? (
        <p className="spo-product-sync-live-hint" role="status">
          商品同步进行中，列表可能稍滞后，可继续查询或其它操作。
        </p>
      ) : null}
      <ProductListCard
        tabs={tabs}
        // 仅列表查询时遮罩；同步中由进度条展示，勿盖「正在查询商品…」
        loading={Boolean(listReady) && syncState.listLoading}
        loadingLabel="正在查询商品…"
      >
        <PlatformProductManagePage
          showToast={showToast}
          embedded
          workspace="shein"
          forcedShopId={pageShopScope || ''}
          shopTypeFilter={expectedShopType || ''}
          sheinExternalToolbar
          opsExternalChrome
          enableSheinOpsToolbar={false}
          hideDimensionTabs
          lockListingDimension={mode !== 'catalog'}
          initialListingDimension={listingDimension}
          controlledListingDimension={listingDimension}
          onListingDimensionChange={setListingDimension}
          controlledQuery={query}
          onDimensionStatsChange={setDimensionStats}
          onSelectionChange={handleSelectionChange}
          onSheinSyncStateChange={handleSyncState}
        />
      </ProductListCard>

      <BatchQueryModal
        open={Boolean(batchModalField)}
        title={batchModalTitle}
        initialValue={batchModalField ? filterDraft[batchModalField] : ''}
        placeholder={batchModalMeta?.placeholder}
        onConfirm={onBatchConfirm}
        onClose={() => setBatchModalField(null)}
      />
      <SheinProductSyncModal
        open={syncModalOpen}
        confirming={syncModalConfirming}
        onClose={() => {
          if (!syncModalConfirming) setSyncModalOpen(false);
        }}
        onConfirm={async (payload) => {
          if (!syncState.refresh) {
            showToast(fixedUi("同步入口未就绪，请稍后重试"), 'warning');
            return;
          }
          const concrete = resolveConcreteShopId(pageShopScope);
          if (!concrete) {
            setPendingSyncPayload(payload);
            setSyncModalOpen(false);
            setShopPickOpen(true);
            return;
          }
          setSyncModalConfirming(true);
          try {
            const started = await syncState.refresh(payload);
            if (started !== false) {
              setSyncModalOpen(false);
            }
          } catch (e) {
            showToast(e?.message || '启动商品同步失败', 'error');
          } finally {
            setSyncModalConfirming(false);
          }
        }}
      />
      <PlatformShopPickModal
        open={shopPickOpen}
        shops={scopedShops}
        title={fixedUi("选择要同步的店铺")}
        description="全部视图下请选择一家店铺后，再调用 SHEIN 平台接口同步。"
        confirmText="继续同步"
        initialShopId={resolveConcreteShopId(pageShopScope) || ''}
        onClose={() => {
          setShopPickOpen(false);
          setPendingSyncPayload(null);
        }}
        onConfirm={async (shopId) => {
          setShopPickOpen(false);
          setPageShopScope(String(shopId));
          // 同步入口依赖 forcedShopId；先切页内范围再打开同步弹窗
          setTimeout(() => {
            setSyncModalOpen(true);
            if (pendingSyncPayload && syncState.refresh) {
              /* 用户确认弹窗后再带参数执行 */
            }
            setPendingSyncPayload(null);
          }, 0);
        }}
      />
    </>
  );
}
